import { useState, useEffect } from 'react';
import { createAPI } from 'edge-stream-js';
import type { ServerInfo, GroupInfo } from 'edge-stream-js';
import { useToast } from 'edge-stream-js-react';
import { ErrorBoundary } from './components';
import { UsersIcon, CalendarIcon, ClockIcon } from '@bizfirst/common-react';

interface ServerDashboardProps {
  apiBaseUrl: string;
  currentUser: string;
  accessToken?: string;
}

export function ServerDashboard({ apiBaseUrl, currentUser, accessToken }: ServerDashboardProps) {
  const api = createAPI(apiBaseUrl, accessToken ? () => accessToken : undefined);
  const toast = useToast();
  const [servers, setServers] = useState<ServerInfo[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    groupName: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);

  // Fetch servers on mount
  useEffect(() => {
    loadServers();
    const interval = setInterval(loadServers, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch groups when server is selected
  useEffect(() => {
    if (selectedServerId) {
      loadGroups();
    }
  }, [selectedServerId]);

  const loadServers = async () => {
    try {
      const serverList = await api.getServers();
      setServers(serverList);

      if (!selectedServerId && serverList.length > 0) {
        setSelectedServerId(serverList[0].serverId);
      }
    } catch (error) {
      console.error('Error loading servers:', error);
    }
  };

  const loadGroups = async () => {
    if (!selectedServerId) return;
    try {
      const groupList = await api.getGroupsByServer(selectedServerId);
      setGroups(groupList);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!selectedServerId || !createFormData.groupName.trim()) {
      toast.warning('Please enter a group name');
      return;
    }

    setLoading(true);
    try {
      const newGroup = await api.createGroup(
        selectedServerId,
        createFormData.groupName,
        createFormData.description,
        currentUser
      );

      if (newGroup) {
        setShowCreateModal(false);
        setCreateFormData({ groupName: '', description: '' });
        await loadGroups();
        toast.success('Group created successfully!');
      } else {
        toast.error('Failed to create group');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Error creating group');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (group: GroupInfo) => {
    try {
      setLoading(true);
      const success = await api.joinGroup(group.serverId, group.groupName, currentUser);

      if (success) {
        toast.success(`Joined group: ${group.groupName}`);
      } else {
        toast.warning('Could not join group - please try again');
      }
    } catch (error) {
      toast.error(`Error joining group: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="dashboard-container">
        <h2>Server Dashboard</h2>

        <div className="dashboard-layout">
        {/* Servers List */}
        <div className="servers-panel">
          <h3>Servers</h3>
          <div className="servers-list">
            {servers.length === 0 ? (
              <p className="empty-state">No servers available</p>
            ) : (
              servers.map((server) => (
                <div
                  key={server.serverId}
                  className={`server-card ${selectedServerId === server.serverId ? 'active' : ''}`}
                  onClick={() => setSelectedServerId(server.serverId)}
                >
                  <div className="server-header">
                    <span className="server-name">{server.serverName || server.serverId}</span>
                    <span className={`status-badge status-${server.status}`}>
                      <span className={`status-dot status-${server.status}`}></span>
                      {server.status}
                    </span>
                  </div>
                  <div className="server-stats">
                    <span>Messages: {server.messageCount}</span>
                    {server.lastEventAt && (
                      <span className="stat-item">
                        <ClockIcon />
                        Last: {new Date(server.lastEventAt).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Groups Panel */}
        <div className="groups-panel">
          <div className="groups-header">
            <h3>Groups {selectedServerId && `for ${selectedServerId}`}</h3>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
              disabled={!selectedServerId}
            >
              + Create Group
            </button>
          </div>

          <div className="groups-list">
            {!selectedServerId ? (
              <p className="empty-state">Select a server to see groups</p>
            ) : groups.length === 0 ? (
              <p className="empty-state">No groups in this server</p>
            ) : (
              groups.map((group) => (
                <div key={group.groupId} className="group-card">
                  <div className="group-header">
                    <h4>{group.groupName}</h4>
                    {group.description && <p className="description">{group.description}</p>}
                  </div>
                  <div className="group-stats">
                    <span className="stat-item">
                      <UsersIcon />
                      Members: {group.memberCount}
                    </span>
                    <span className="stat-item">
                      <CalendarIcon />
                      Created: {new Date(group.createdAt).toLocaleDateString()}
                    </span>
                    {group.lastActivityAt && <span className="stat-item">
                      <ClockIcon />
                      Last Activity: {new Date(group.lastActivityAt).toLocaleTimeString()}
                    </span>}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleJoinGroup(group)}
                  >
                    Join Group
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Group</h3>

            <div className="form-group">
              <label>Group Name *</label>
              <input
                type="text"
                value={createFormData.groupName}
                onChange={(e) => setCreateFormData({ ...createFormData, groupName: e.target.value })}
                placeholder="e.g., Sales Team, Support Chat"
                className="input"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={createFormData.description}
                onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                placeholder="Optional description for the group"
                className="textarea"
                rows={3}
                disabled={loading}
              />
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateGroup}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
